"use client";

import { FormEvent, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

type FormState = {
  name: string;
  email: string;
  company: string;
  phone: string;
  help: string;
  budget: string;
  message: string;
};

const initial: FormState = {
  name: "",
  email: "",
  company: "",
  phone: "",
  help: "",
  budget: "",
  message: "",
};

export function ContactForm({ className }: { className?: string }) {
  const [values, setValues] = useState<FormState>(initial);
  const [errors, setErrors] = useState<Partial<Record<keyof FormState, string>>>({});
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  const validate = () => {
    const next: Partial<Record<keyof FormState, string>> = {};
    if (!values.name.trim()) next.name = "Name is required";
    if (!values.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(values.email)) {
      next.email = "Valid work email is required";
    }
    if (!values.company.trim()) next.company = "Company is required";
    if (!values.help.trim()) next.help = "Please tell us how we can help";
    if (!values.message.trim()) next.message = "Message is required";
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!validate()) return;
    setSubmitting(true);
    try {
      const res = await fetch("/api/contact", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(values),
      });
      if (!res.ok) {
        const data = (await res.json().catch(() => null)) as { error?: string } | null;
        setErrors({ message: data?.error ?? "Something went wrong. Please try again." });
        return;
      }
      setSuccess(true);
      setValues(initial);
    } catch {
      setErrors({ message: "Unable to reach the server. Please try again." });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className={cn("relative", className)}>
      <AnimatePresence>
        {success ? (
          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            className="rounded-[2rem] border border-border bg-card p-10 text-center"
          >
            <CheckCircle2 className="mx-auto h-12 w-12 text-success" />
            <h3 className="mt-4 font-display text-3xl tracking-tight">Briefing received</h3>
            <p className="mt-3 text-muted">
              Thank you. A Maxridge lead will follow up within one business day with next steps.
            </p>
            <button
              type="button"
              className="mt-6 text-sm font-medium text-accent"
              onClick={() => setSuccess(false)}
            >
              Send another message
            </button>
          </motion.div>
        ) : (
          <motion.form
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            onSubmit={onSubmit}
            className="grid gap-4 rounded-[2rem] border border-border bg-card p-6 md:p-8"
            noValidate
          >
            <Field
              label="Name"
              value={values.name}
              error={errors.name}
              onChange={(v) => setValues((s) => ({ ...s, name: v }))}
            />
            <Field
              label="Work Email"
              type="email"
              value={values.email}
              error={errors.email}
              onChange={(v) => setValues((s) => ({ ...s, email: v }))}
            />
            <div className="grid gap-4 sm:grid-cols-2">
              <Field
                label="Company"
                value={values.company}
                error={errors.company}
                onChange={(v) => setValues((s) => ({ ...s, company: v }))}
              />
              <Field
                label="Phone"
                value={values.phone}
                onChange={(v) => setValues((s) => ({ ...s, phone: v }))}
              />
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <SelectField
                label="What can we help you with?"
                value={values.help}
                error={errors.help}
                onChange={(v) => setValues((s) => ({ ...s, help: v }))}
                options={[
                  "AI & Machine Learning",
                  "Data Platform",
                  "Cloud & DevOps",
                  "Enterprise Software",
                  "Digital Transformation",
                  "Other",
                ]}
              />
              <SelectField
                label="Budget / Engagement Type"
                value={values.budget}
                onChange={(v) => setValues((s) => ({ ...s, budget: v }))}
                options={[
                  "Discovery workshop",
                  "Pilot / POC",
                  "Full program",
                  "Retainer partnership",
                  "Not sure yet",
                ]}
              />
            </div>
            <div>
              <label className="mb-2 block text-xs uppercase tracking-[0.14em] text-muted">Message</label>
              <textarea
                rows={5}
                value={values.message}
                onChange={(e) => setValues((s) => ({ ...s, message: e.target.value }))}
                data-gramm="false"
                data-gramm_editor="false"
                data-enable-grammarly="false"
                suppressHydrationWarning
                className="w-full rounded-2xl border border-border bg-background px-4 py-3 text-sm outline-none focus:border-accent"
              />
              {errors.message ? <p className="mt-1 text-xs text-danger">{errors.message}</p> : null}
            </div>
            <Button type="submit" size="lg" variant="secondary" disabled={submitting} className="w-full sm:w-auto">
              {submitting ? "Sending..." : "Let's build what's next"}
            </Button>
          </motion.form>
        )}
      </AnimatePresence>
    </div>
  );
}

function Field({
  label,
  value,
  onChange,
  error,
  type = "text",
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  error?: string;
  type?: string;
}) {
  return (
    <div>
      <label className="mb-2 block text-xs uppercase tracking-[0.14em] text-muted">{label}</label>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        autoComplete={type === "email" ? "email" : "on"}
        data-gramm="false"
        data-gramm_editor="false"
        data-enable-grammarly="false"
        suppressHydrationWarning
        className="h-12 w-full rounded-full border border-border bg-background px-4 text-sm outline-none focus:border-accent"
      />
      {error ? <p className="mt-1 text-xs text-danger">{error}</p> : null}
    </div>
  );
}

function SelectField({
  label,
  value,
  onChange,
  options,
  error,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  options: string[];
  error?: string;
}) {
  return (
    <div>
      <label className="mb-2 block text-xs uppercase tracking-[0.14em] text-muted">{label}</label>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="h-12 w-full rounded-full border border-border bg-background px-4 text-sm outline-none focus:border-accent"
      >
        <option value="">Select</option>
        {options.map((option) => (
          <option key={option} value={option}>
            {option}
          </option>
        ))}
      </select>
      {error ? <p className="mt-1 text-xs text-danger">{error}</p> : null}
    </div>
  );
}